import { FC, useState } from 'react';
import { Button, Dropdown, Form, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { TbClock } from 'react-icons/tb';
import { FiPlusCircle } from 'react-icons/fi';
import moment from 'moment';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { convertHrsMinsToMins } from 'utils/string.utils';
import timeTracking from 'services/timeTracking';
import TimerContainer from './TimerContainer';
import { requestTimelogSlice } from '../../modules/request/store/requestTimelog';
import { setGlobalLogTrigger } from '../../store/container/globalLog/globalLog-slice';

interface RequestTimerDropdownProps {
  request: Record<string, any>;
  onAdded?: (timelog: any) => void;
  icon?: 'plus' | 'clock';
  autoWidth?: boolean;
}

const timerIcons = {
  plus: <FiPlusCircle />,
  clock: <TbClock />,
};

const RequestTimerDropdown: FC<RequestTimerDropdownProps> = ({
  request,
  onAdded,
  icon = 'clock',
  autoWidth = false,
}) => {
  const user = useSelector((store: any) => store.auth.user);

  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const onAddTimeLog = async () => {
    setSaving(true);

    try {
      const formData = await form.validateFields();

      const entry = await timeTracking.createEntry({
        source: 'request',
        sourceUuid: request.uuid,
        userUuid: formData.user_id ?? user.uuid,
        date: moment(formData.date).format('YYYY-MM-DD'),
        minutes: convertHrsMinsToMins(formData.time),
        description: formData.description,
      });

      form.resetFields();

      setVisible(false);

      dispatch(requestTimelogSlice.actions.fetch());

      dispatch(setGlobalLogTrigger());

      onAdded?.(entry);
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {visible && (
        <div
          style={{
            height: '100vh',
            width: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 100,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(false);
          }}
        />
      )}

      <Dropdown
        visible={visible}
        placement="bottomCenter"
        overlay={
          <TimerContainer
            style={{ width: 480 }}
            form={form}
            projectId={request.project.uuid}
            modalStatus={request.status}
            loading={saving}
            showUserSelection="request"
            onAddEntry={onAddTimeLog}
            onCancel={() => {
              setVisible(false);
            }}
          />
        }
      >
        <Tooltip title="Add timelog">
          <Button
            type="link"
            icon={timerIcons[icon]}
            onClick={() => setVisible(true)}
            style={{ height: 28, width: autoWidth ? 'auto' : 32 }}
          />
        </Tooltip>
      </Dropdown>
    </>
  );
};

export default RequestTimerDropdown;
